import { useCallback, useEffect, useState } from "react";
import { Plus, Truck, Printer, Pencil, XCircle, PackageCheck, Loader2, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { API, backendUrl, inr } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS_STYLE = {
  requested: "bg-amber-100 text-amber-700 hover:bg-amber-100",
  dispatched: "bg-blue-100 text-blue-700 hover:bg-blue-100",
  received: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100",
  cancelled: "bg-slate-100 text-slate-500 hover:bg-slate-100",
};

const emptyForm = { quote_id: "", note: "", items: [] };
const emptyItem = { product_id: "", qty: 1, rate: 0 };

export default function Challans() {
  const { user } = useAuth();
  const [challans, setChallans] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [b2bId, setB2bId] = useState("");
  const [defaultDispatchWh, setDefaultDispatchWh] = useState("");
  const [firmFilter, setFirmFilter] = useState("all");
  const [firms, setFirms] = useState([]);
  const [tab, setTab] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [receiveCh, setReceiveCh] = useState(null);
  const [receiveQtys, setReceiveQtys] = useState({});
  const [receiving, setReceiving] = useState(false);
  const [dispatchCh, setDispatchCh] = useState(null);
  const [dispatchWh, setDispatchWh] = useState("");
  const [acting, setActing] = useState("");

  const canCreate = ["admin", "accountant", "sales", "staff", "stock_team", "stock_head"].includes(user?.role);
  const canManage = ["admin", "accountant"].includes(user?.role);
  const canDispatch = ["admin", "stock_team", "stock_head"].includes(user?.role);

  const load = useCallback(async () => {
    const params = {};
    if (tab) params.status = tab;
    if (firmFilter !== "all") params.firm = firmFilter;
    const [ch, wh, st, cfg] = await Promise.all([
      API.get("/challans", { params }),
      API.get("/warehouses"),
      API.get("/inventory/stock"),
      API.get("/settings/config"),
    ]);
    setChallans(ch.data);
    setWarehouses(wh.data);
    setProducts(st.data);
    setB2bId(cfg.data.b2b_warehouse_id || "");
    API.get("/documents", { params: { type: "quotation" } }).then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  }, [tab, firmFilter]);

  useEffect(() => {
    API.get("/firms").then((r) => setFirms(r.data)).catch(() => {});
    API.get("/settings/config")
      .then(async (c) => {
        const { data: fs } = await API.get("/firms");
        const f1 = fs.find((f) => f.id === c.data.firm1_id);
        if (f1?.dispatch_warehouse_id) setDefaultDispatchWh(f1.dispatch_warehouse_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(e.response?.data?.detail || "Failed to load challans"));
  }, [load]);

  const srcWarehouses = warehouses.filter((w) => w.id !== b2bId);
  const b2bName = warehouses.find((w) => w.id === b2bId)?.name || "B2B warehouse";

  const productRate = (pid) => {
    const p = products.find((x) => x.id === pid);
    return p?.b2b_price ?? 0;
  };

  const linkQuote = (qid) => {
    setForm((f) => {
      const q = quotes.find((x) => x.id === qid);
      if (!q) return { ...f, quote_id: qid };
      const items = (q.items || [])
        .filter((i) => i.product_id)
        .map((i) => ({ product_id: i.product_id, qty: i.qty, rate: productRate(i.product_id) || i.rate || 0 }));
      return { ...f, quote_id: qid, items: items.length ? items : f.items };
    });
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm, items: [{ ...emptyItem }] });
    setOpen(true);
  };

  const openEdit = (ch) => {
    setEditId(ch.id);
    setForm({
      quote_id: ch.quote_id || "",
      note: ch.note || "",
      items: ch.items.map((i) => ({ product_id: i.product_id, qty: i.qty, rate: i.rate })),
    });
    setOpen(true);
  };

  const setItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));

  const pickProduct = (i, pid) => setItem(i, { product_id: pid, rate: productRate(pid) });

  const save = async () => {
    if (saving) return;
    if (!form.items.length || form.items.some((i) => !i.product_id || !(Number(i.qty) > 0))) {
      return toast.error("Each item needs a product and qty");
    }
    setSaving(true);
    try {
      const payload = {
        quote_id: form.quote_id || "",
        note: form.note,
        items: form.items.map((i) => ({ product_id: i.product_id, qty: Number(i.qty), rate: Number(i.rate) || 0 })),
      };
      if (editId) {
        await API.patch(`/challans/${editId}`, payload);
        toast.success("Challan updated");
      } else {
        await API.post("/challans", payload);
        toast.success("Challan created — stock team will pick the warehouse at dispatch");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save challan");
    } finally {
      setSaving(false);
    }
  };

  const confirmDispatch = async () => {
    if (!dispatchCh || acting) return;
    if (!dispatchCh.from_warehouse_id && !dispatchWh) return toast.error("Select the source warehouse");
    setActing(dispatchCh.id);
    try {
      await API.post(`/challans/${dispatchCh.id}/dispatch`, { warehouse_id: dispatchWh });
      toast.success(`Challan ${dispatchCh.number} dispatched — goods in transit`);
      setDispatchCh(null);
      setDispatchWh("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dispatch failed");
    } finally {
      setActing("");
    }
  };

  const doCancel = async (ch) => {
    if (acting) return;
    setActing(ch.id);
    try {
      await API.post(`/challans/${ch.id}/cancel`);
      toast.success(`Challan ${ch.number} cancelled`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setActing("");
    }
  };

  const openReceive = (ch) => {
    setReceiveCh(ch);
    setReceiveQtys(Object.fromEntries(ch.items.map((i) => [i.product_id, i.qty])));
  };

  const confirmReceive = async () => {
    if (receiving || !receiveCh) return;
    setReceiving(true);
    try {
      const items = receiveCh.items.map((i) => ({ product_id: i.product_id, received_qty: Number(receiveQtys[i.product_id] ?? i.qty) }));
      const { data } = await API.post(`/challans/${receiveCh.id}/receive`, { items });
      toast.success(`Goods received — Purchase Bill ${data.purchase_bill_number} created`);
      setReceiveCh(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm receipt");
    } finally {
      setReceiving(false);
    }
  };

  const totalOf = (ch) => ch.items.reduce((a, i) => a + i.qty * i.rate, 0);
  const itemsSummary = (ch) => {
    const parts = ch.items.slice(0, 2).map((i) => `${i.name} ×${i.qty}`);
    return parts.join(", ") + (ch.items.length > 2 ? ` +${ch.items.length - 2} more` : "");
  };

  return (
    <div className="space-y-6" data-testid="challans-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Delivery Challans</h1>
          <p className="text-sm text-slate-500">Stock transfers from Company 1 (B2C) to {b2bName} (B2B)</p>
        </div>
        {canCreate && (
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={openCreate} disabled={!b2bId} data-testid="new-challan-button">
            <Plus className="mr-1 h-4 w-4" /> New Challan
          </Button>
        )}
      </div>

      {!b2bId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="b2b-not-configured">
          B2B warehouse is not configured yet — ask the admin to set it in Settings.
        </div>
      )}

      {canManage && firms.length > 1 && (
        <div className="flex justify-end">
          <Select value={firmFilter} onValueChange={setFirmFilter}>
            <SelectTrigger className="h-9 w-52" data-testid="challan-firm-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="challan-firm-all">All Firms</SelectItem>
              <SelectItem value="firm1" data-testid="challan-firm-1">{(firms.find((f) => /b2c/i.test(f.name)) || firms[0] || {}).name || "Company 1"}</SelectItem>
              <SelectItem value="firm2" data-testid="challan-firm-2">{(firms.find((f) => /b2b/i.test(f.name)) || firms[1] || {}).name || "Company 2"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <Tabs value={tab || "all"} onValueChange={(v) => setTab(v === "all" ? "" : v)}>
        <TabsList data-testid="challan-status-tabs">
          <TabsTrigger value="all" data-testid="challan-tab-all">All</TabsTrigger>
          <TabsTrigger value="requested" data-testid="challan-tab-requested">Requested</TabsTrigger>
          <TabsTrigger value="dispatched" data-testid="challan-tab-dispatched">Dispatched</TabsTrigger>
          <TabsTrigger value="received" data-testid="challan-tab-received">Received</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="challan-tab-cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-lg border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Challan No</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Products</TableHead>
              <TableHead>Ref</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="challans-table">
            {challans.map((ch) => (
              <TableRow key={ch.id} data-testid={`challan-row-${ch.id}`}>
                <TableCell className="font-medium">{ch.number}</TableCell>
                <TableCell>{ch.date}</TableCell>
                <TableCell className="max-w-56" data-testid={`challan-items-${ch.id}`}>
                  <div className="truncate text-sm text-slate-800">{itemsSummary(ch)}</div>
                  <div className="text-[11px] text-slate-400">
                    {ch.from_warehouse_name ? `from ${ch.from_warehouse_name}` : "warehouse chosen at dispatch"} → {ch.to_warehouse_name}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-slate-500">
                  {ch.quote_number ? (
                    <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3" /> {ch.quote_number}{ch.customer_name ? ` · ${ch.customer_name}` : ""}</span>
                  ) : "-"}
                </TableCell>
                <TableCell className="text-right">{inr(totalOf(ch))}</TableCell>
                <TableCell>
                  <Badge className={STATUS_STYLE[ch.status]} data-testid={`challan-status-${ch.id}`}>{["stock_team", "stock_head"].includes(user?.role) ? (ch.status === "dispatched" ? "Pending Approval" : ch.status === "received" ? "Approved" : ch.status) : ch.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{ch.created_by_name}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(`${backendUrl}/api/challans/${ch.id}/pdf`, "_blank")} data-testid={`challan-print-${ch.id}`} aria-label="Print challan">
                      <Printer className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    {ch.status === "requested" && canManage && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(ch)} data-testid={`challan-edit-${ch.id}`} aria-label="Edit challan">
                        <Pencil className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                    )}
                    {ch.status === "requested" && canDispatch && (
                      <Button size="sm" variant="outline" className="h-7 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => { setDispatchCh(ch); setDispatchWh(ch.from_warehouse_id || defaultDispatchWh || srcWarehouses[0]?.id || ""); }} data-testid={`challan-dispatch-${ch.id}`}>
                        <Truck className="mr-1 h-3.5 w-3.5" /> Dispatch
                      </Button>
                    )}
                    {ch.status === "dispatched" && canManage && (
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => openReceive(ch)} data-testid={`challan-receive-${ch.id}`}>
                        <PackageCheck className="mr-1 h-3.5 w-3.5" /> Approve Transfer
                      </Button>
                    )}
                    {["requested", "dispatched"].includes(ch.status) && canManage && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" disabled={acting === ch.id} onClick={() => doCancel(ch)} data-testid={`challan-cancel-${ch.id}`} aria-label="Cancel challan">
                        <XCircle className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {challans.length === 0 && (
              <TableRow><TableCell colSpan={8}><EmptyState compact title="No challans yet" /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="challan-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Challan" : "New Delivery Challan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Link Performa Invoice (optional)</Label>
              <Select value={form.quote_id || "none"} onValueChange={(v) => linkQuote(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="challan-quote-select"><SelectValue placeholder="No link" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No link</SelectItem>
                  {quotes.map((q) => (
                    <SelectItem key={q.id} value={q.id}>{q.number} — {q.customer?.name || ""} ({inr(q.total)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-slate-400">Auto-fills items from the linked document · warehouse is picked by the stock team at dispatch · destination: {b2bName}</p>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Items</Label>
                <Button size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }))} data-testid="add-challan-item-button">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add item
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="flex items-end gap-2 rounded-md border border-slate-200 p-2.5" data-testid={`challan-item-row-${i}`}>
                    <div className="flex-1">
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Product</div>
                      <Select value={it.product_id} onValueChange={(v) => pickProduct(i, v)}>
                        <SelectTrigger data-testid={`challan-item-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                        <SelectContent>
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (stock: {p.total_stock} {p.unit})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20">
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Qty</div>
                      <Input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} data-testid={`challan-item-qty-${i}`} />
                    </div>
                    <div className="w-24">
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Rate ₹</div>
                      <Input type="number" min="0" value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} data-testid={`challan-item-rate-${i}`} />
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} data-testid={`challan-item-remove-${i}`} aria-label="Remove item">
                      <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Order ref / transport details" className="mt-1.5" data-testid="challan-note-input" />
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid="save-challan-button">
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {saving ? "Saving..." : editId ? "Save Changes" : "Create Challan"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dispatchCh} onOpenChange={(o) => !o && setDispatchCh(null)}>
        <DialogContent data-testid="dispatch-dialog">
          <DialogHeader>
            <DialogTitle>Dispatch {dispatchCh?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Stock stays in transit until accounts confirm receipt. Pick the Company 1 warehouse the goods are leaving from.</p>
            <div>
              <Label>From Warehouse (Company 1)</Label>
              <Select value={dispatchWh} onValueChange={setDispatchWh}>
                <SelectTrigger className="mt-1.5" data-testid="dispatch-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {srcWarehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={confirmDispatch} disabled={!!acting} data-testid="dispatch-confirm-button">
              {acting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              <Truck className="mr-1.5 h-4 w-4" /> Mark Dispatched
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receiveCh} onOpenChange={(o) => !o && setReceiveCh(null)}>
        <DialogContent data-testid="receive-dialog">
          <DialogHeader>
            <DialogTitle>Approve Transfer — {receiveCh?.number}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Enter the quantity actually received. This deducts Company 1 stock, adds it to the B2B warehouse, and creates the Purchase Bill.</p>
          <div className="space-y-2">
            {receiveCh?.items.map((i) => (
              <div key={i.product_id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-2.5">
                <div className="text-sm">
                  <div className="font-medium text-slate-800">{i.name}</div>
                  <div className="text-xs text-slate-400">dispatched: {i.qty} {i.unit}</div>
                </div>
                <Input type="number" min="0" max={i.qty} className="w-24" value={receiveQtys[i.product_id] ?? i.qty} onChange={(e) => setReceiveQtys((p) => ({ ...p, [i.product_id]: e.target.value }))} data-testid={`receive-qty-${i.product_id}`} />
              </div>
            ))}
          </div>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmReceive} disabled={receiving} data-testid="receive-confirm-button">
            {receiving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {receiving ? "Posting..." : "Approve Transfer & Create Purchase Bill"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
